import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import './AccountTransactions.css';

function AccountTransactions({ account, onBack }) {
  const [allTransactions, setAllTransactions] = useState([]); // All loaded transactions
  const [displayedTransactions, setDisplayedTransactions] = useState([]); // Paginated display
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filterPeriod, setFilterPeriod] = useState('this-month');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [showMonthSelector, setShowMonthSelector] = useState(false);
  const [monthDropdownOpen, setMonthDropdownOpen] = useState(false);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [accountBalance, setAccountBalance] = useState(0);
  const TRANSACTIONS_PER_PAGE = 400;
  const user = auth.currentUser;

  // Load account balance from Firestore
  useEffect(() => {
    if (!user || !account) return;
    
    const balancesRef = doc(db, 'users', user.uid, 'balances', 'balances');
    const unsubscribe = onSnapshot(balancesRef, (snapshot) => {
      if (snapshot.exists()) {
        const balances = snapshot.data();
        const balance = balances[account.name] || 0;
        setAccountBalance(balance);
      }
    });
    
    return unsubscribe;
  }, [user, account]);

  // Discover all months that actually have data for this account
  useEffect(() => {
    if (!user || !account) return;

    const discoverMonths = async () => {
      setLoading(true);
      const monthsWithData = [];
      const currentDate = new Date();
      
      // Check last 5 years of months
      for (let i = 0; i < 60; i++) {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const monthKey = `${year}-${month}`;
        
        try {
          const transactionsRef = collection(db, 'users', user.uid, 'transactions', monthKey, 'items');
          const snapshot = await getDocs(transactionsRef);
          
          // Check if this month has any transactions for this account
          const hasAccountTransactions = snapshot.docs.some(doc => {
            const data = doc.data();
            return data.account === account.name;
          });
          
          if (hasAccountTransactions) {
            monthsWithData.push(monthKey);
          }
        } catch (error) {
          // Month doesn't exist, continue
        }
      }
      
      setAvailableMonths(monthsWithData);
      
      // Load initial transactions
      if (monthsWithData.length > 0) {
        await loadTransactionsForMonths(monthsWithData);
      }
      
      setLoading(false);
    };
    
    discoverMonths();
  }, [user, account]);

  // Load all transactions for specified months
  const loadTransactionsForMonths = async (monthKeys) => {
    try {
      const transactions = [];
      
      for (const monthKey of monthKeys) {
        try {
          const transactionsRef = collection(db, 'users', user.uid, 'transactions', monthKey, 'items');
          const snapshot = await getDocs(transactionsRef);
          
          snapshot.docs.forEach(doc => {
            const txnData = { id: doc.id, monthKey, ...doc.data() };
            // Filter by account name
            if (txnData.account === account.name) {
              transactions.push(txnData);
            }
          });
        } catch (error) {
          console.log(`Error loading ${monthKey}`);
        }
      }
      
      // Sort all transactions by date (most recent first)
      transactions.sort((a, b) => {
        if (!a.date) return 1;
        if (!b.date) return -1;
        
        const parseDate = (dateStr) => {
          const parts = dateStr.split('-');
          if (parts.length !== 3) return new Date(0);
          return new Date(parts[2], parts[1] - 1, parts[0]);
        };
        
        return parseDate(b.date) - parseDate(a.date);
      });
      
      setAllTransactions(transactions);
      // Show first page
      setDisplayedTransactions(transactions.slice(0, TRANSACTIONS_PER_PAGE));
      setCurrentPage(1);
    } catch (error) {
      console.error('Error loading transactions:', error);
    }
  };

  const handleLoadMore = () => {
    setLoadingMore(true);
    const startIndex = currentPage * TRANSACTIONS_PER_PAGE;
    const endIndex = startIndex + TRANSACTIONS_PER_PAGE;
    const nextBatch = allTransactions.slice(startIndex, endIndex);
    
    setDisplayedTransactions(prev => [...prev, ...nextBatch]);
    setCurrentPage(prev => prev + 1);
    setLoadingMore(false);
  };

  const hasMore = displayedTransactions.length < allTransactions.length;

  const getFilteredTransactions = () => {
    let filtered = [...displayedTransactions];

    // Filter by period
    if (filterPeriod === 'this-month') {
      const now = new Date();
      const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      filtered = filtered.filter(t => t.monthKey === currentMonthKey);
    } else if (filterPeriod === 'last-month') {
      const lastMonth = new Date();
      lastMonth.setMonth(lastMonth.getMonth() - 1);
      const lastMonthKey = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}`;
      filtered = filtered.filter(t => t.monthKey === lastMonthKey);
    } else if (filterPeriod === 'custom' && selectedMonths.length > 0) {
      filtered = filtered.filter(t => selectedMonths.includes(t.monthKey));
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(t => 
        t.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.amount?.toString().includes(searchQuery)
      );
    }

    return filtered;
  };

  const filteredTransactions = getFilteredTransactions();

  const handlePeriodChange = (value) => {
    setFilterPeriod(value);
    if (value === 'custom') {
      setShowMonthSelector(true);
      setMonthDropdownOpen(true);
    } else {
      setShowMonthSelector(false);
      setMonthDropdownOpen(false);
      setSelectedMonths([]);
    }
  };

  const toggleMonth = (monthKey) => {
    setSelectedMonths(prev => {
      if (prev.includes(monthKey)) {
        return prev.filter(m => m !== monthKey);
      } else {
        return [...prev, monthKey];
      }
    });
  };

  const selectAllMonths = () => {
    setSelectedMonths([...availableMonths]);
  };

  const clearAllMonths = () => {
    setSelectedMonths([]);
  };

  const formatMonthKey = (monthKey) => {
    if (!monthKey) return '';
    const [year, month] = monthKey.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  const totalInflow = filteredTransactions
    .filter(t => (parseFloat(t.amount) || 0) > 0)
    .reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);

  const totalOutflow = filteredTransactions
    .filter(t => (parseFloat(t.amount) || 0) < 0)
    .reduce((sum, t) => sum + Math.abs(parseFloat(t.amount) || 0), 0);

  const formatBalance = (balance) => {
    return balance.toLocaleString('en-GB', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Input format: DD-MM-YYYY (from your transactions)
    const parts = dateString.split('-');
    if (parts.length !== 3) return dateString;
    
    const day = parts[0];
    const month = parts[1];
    const year = parts[2];
    
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthIndex = parseInt(month) - 1;
    
    return `${day} ${monthNames[monthIndex]} ${year}`;
  };

  const formatCurrency = (amount) => {
    const num = Math.abs(parseFloat(amount) || 0);
    return num.toLocaleString('en-GB', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Group transactions by month
  const groupedTransactions = filteredTransactions.reduce((acc, txn) => {
    const monthKey = txn.monthKey;
    if (!acc[monthKey]) {
      acc[monthKey] = [];
    }
    acc[monthKey].push(txn);
    return acc;
  }, {});

  const sortedMonths = Object.keys(groupedTransactions).sort((a, b) => b.localeCompare(a));

  return (
    <div className="account-transactions">
      {/* Header */}
      <div className="account-header">
        <button className="btn-back" onClick={onBack}>
          ← Back to Accounts
        </button>
        <div className="account-info">
          <h2>{account.name}</h2>
          <p className="account-type">{account.type}</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="transaction-summary">
        <div className="summary-card balance">
          <div className="summary-label">Current Balance</div>
          <div className="summary-value">£{formatBalance(accountBalance)}</div>
        </div>
        <div className="summary-card inflow">
          <div className="summary-label">Total Inflow</div>
          <div className="summary-value positive">+£{formatBalance(totalInflow)}</div>
        </div>
        <div className="summary-card outflow">
          <div className="summary-label">Total Outflow</div>
          <div className="summary-value negative">-£{formatBalance(totalOutflow)}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="transactions-filters">
        <div className="filter-group">
          <input
            type="text"
            className="search-input"
            placeholder="Search transactions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select 
            className="filter-select"
            value={filterPeriod}
            onChange={(e) => handlePeriodChange(e.target.value)}
          >
            <option value="all">All Time</option>
            <option value="this-month">This Month</option>
            <option value="last-month">Last Month</option>
            <option value="custom">Custom</option>
          </select>
        </div>
        
        {/* Custom Month Selector */}
        {showMonthSelector && (
          <div className="filter-group custom-months-filter">
            <div className="custom-months-dropdown">
              <button 
                className="custom-months-dropdown-btn"
                onClick={() => setMonthDropdownOpen(!monthDropdownOpen)}
                type="button"
              >
                {selectedMonths.length === 0 ? 'Select Months' : `${selectedMonths.length} month${selectedMonths.length > 1 ? 's' : ''} selected`}
                <span className="dropdown-arrow">{monthDropdownOpen ? '▲' : '▼'}</span>
              </button>
              {monthDropdownOpen && (
                <div className="custom-months-dropdown-content">
                  <div className="month-actions">
                    <button type="button" onClick={selectAllMonths}>Select All</button>
                    <button type="button" onClick={clearAllMonths}>Clear All</button>
                  </div>
                  <div className="month-list">
                    {availableMonths.map(monthKey => (
                      <label key={monthKey} className="month-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedMonths.includes(monthKey)}
                          onChange={() => toggleMonth(monthKey)}
                        />
                        <span>{formatMonthKey(monthKey)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        
        <div className="transaction-count">
          {filteredTransactions.length} transactions
        </div>
      </div>

      {/* Transactions List */}
      {loading ? (
        <div className="loading-state">
          Loading transactions...
        </div>
      ) : filteredTransactions.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">💳</div>
          <h3>No transactions found</h3>
          <p>There are no transactions for this account yet.</p>
        </div>
      ) : (
        <>
          <div className="transactions-timeline">
            {sortedMonths.map(monthKey => (
              <div key={monthKey} className="month-group">
                <div className="month-header">
                  <div className="month-dot"></div>
                  <h4 className="month-label">{formatMonthKey(monthKey)}</h4>
                  <div className="month-total">
                    {(() => {
                      const total = groupedTransactions[monthKey].reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);
                      return total >= 0 ? '+' : '-';
                    })()}
                    £{formatBalance(Math.abs(groupedTransactions[monthKey].reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0)))}
                  </div>
                </div>
                
                <div className="transactions-list">
                  {groupedTransactions[monthKey].map(transaction => (
                    <div key={transaction.id} className="transaction-item">
                      <div className="transaction-icon">
                        {(parseFloat(transaction.amount) || 0) > 0 ? '↓' : '↑'}
                      </div>
                      <div className="transaction-details">
                        <div className="transaction-description">
                          {transaction.name || transaction.description || 'No description'}
                        </div>
                        <div className="transaction-category">
                          {transaction.category || 'Uncategorized'} • {formatDate(transaction.date)}
                        </div>
                      </div>
                      <div className={`transaction-amount ${(parseFloat(transaction.amount) || 0) > 0 ? 'positive' : 'negative'}`}>
                        {(parseFloat(transaction.amount) || 0) > 0 ? '+' : '-'}£{formatCurrency(transaction.amount)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Load More Button */}
          {hasMore && (
            <div className="load-more-container">
              <button 
                className="btn-load-more" 
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading...' : 'Load More'}
              </button>
              <p className="load-info">
                Showing {displayedTransactions.length} of {allTransactions.length} transactions
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AccountTransactions;